import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import './Accounting.css'

const accountingData = {
  overview: {
    cashPosition: '$124,500.00',
    monthlyRevenue: '$48,291.00',
    monthlyExpenses: '$31,200.00',
    profitMargin: '42.3%',
    uncategorized: 12
  },
  recentTransactions: [
    { id: 1, description: 'Payment from Widget Co', category: 'Revenue', amount: 450.00, type: 'income', date: 'Today' },
    { id: 2, description: 'AWS Cloud Services', category: 'Software', amount: -342.00, type: 'expense', date: 'Today' },
    { id: 3, description: 'Payment from Acme Industries', category: 'Revenue', amount: 1250.00, type: 'income', date: 'Yesterday' },
    { id: 4, description: 'Office Supplies', category: 'Operations', amount: -89.00, type: 'expense', date: 'Yesterday' },
    { id: 5, description: 'Contractor Payment', category: 'Labor', amount: -1500.00, type: 'expense', date: '2 days ago' }
  ],
  expenseBreakdown: [
    { category: 'Software & Tools', amount: 4200, percentage: 35 },
    { category: 'Labor & Contractors', amount: 2800, percentage: 23 },
    { category: 'Marketing', amount: 2100, percentage: 18 },
    { category: 'Operations', amount: 1500, percentage: 12 },
    { category: 'Other', amount: 1400, percentage: 12 }
  ]
}

function AccountingDashboard() {
  return (
    <div className="accounting-dashboard">
      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-label">Cash Position</span>
          <span className="stat-value">{accountingData.overview.cashPosition}</span>
          <span className="stat-sub">Across all accounts</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Monthly Revenue</span>
          <span className="stat-value">{accountingData.overview.monthlyRevenue}</span>
          <span className="stat-sub positive">+8.2% vs last month</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Monthly Expenses</span>
          <span className="stat-value">{accountingData.overview.monthlyExpenses}</span>
          <span className="stat-sub">+3.1% vs last month</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Profit Margin</span>
          <span className="stat-value">{accountingData.overview.profitMargin}</span>
          <span className="stat-sub positive">Healthy</span>
        </div>
      </div>

      {/* Alert Banner */}
      {accountingData.overview.uncategorized > 0 && (
        <div className="alert-banner">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span>{accountingData.overview.uncategorized} transactions need to be categorized</span>
          <Link to="/dashboard/accounting/transactions" className="alert-action">Review now</Link>
        </div>
      )}

      {/* Two Column Grid */}
      <div className="accounting-grid">
        {/* Recent Transactions */}
        <div className="transactions-section">
          <div className="section-header">
            <h2 className="section-title">Recent Transactions</h2>
            <Link to="/dashboard/accounting/transactions" className="section-link">View all</Link>
          </div>
          <div className="transactions-list">
            {accountingData.recentTransactions.map(txn => (
              <div key={txn.id} className="transaction-item">
                <div className="txn-info">
                  <span className="txn-description">{txn.description}</span>
                  <span className="txn-category">{txn.category} · {txn.date}</span>
                </div>
                <span className={`txn-amount ${txn.type}`}>
                  {txn.type === 'income' ? '+' : ''}{txn.amount < 0 ? '-' : ''}${Math.abs(txn.amount).toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Expense Breakdown */}
        <div className="expenses-section">
          <div className="section-header">
            <h2 className="section-title">Expense Breakdown</h2>
            <Link to="/dashboard/accounting/reports" className="section-link">View report</Link>
          </div>
          <div className="expenses-list">
            {accountingData.expenseBreakdown.map((item, idx) => (
              <div key={idx} className="expense-item">
                <div className="expense-info">
                  <span className="expense-category">{item.category}</span>
                  <span className="expense-amount">${item.amount.toLocaleString()}</span>
                </div>
                <div className="expense-bar">
                  <div className="expense-fill" style={{ width: `${item.percentage}%` }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2 className="section-title">Quick Actions</h2>
        <div className="actions-grid">
          <button className="action-card">
            <div className="action-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </div>
            <span className="action-label">Record expense</span>
          </button>
          <button className="action-card">
            <div className="action-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
              </svg>
            </div>
            <span className="action-label">Categorize transactions</span>
          </button>
          <button className="action-card">
            <div className="action-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
            </div>
            <span className="action-label">Generate report</span>
          </button>
          <button className="action-card">
            <div className="action-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="1" y="4" width="22" height="16" rx="2"/>
                <line x1="1" y1="10" x2="23" y2="10"/>
              </svg>
            </div>
            <span className="action-label">Connect bank</span>
          </button>
        </div>
      </div>
    </div>
  )
}

function Accounting() {
  const location = useLocation()
  const isOverview = location.pathname === '/dashboard/accounting'

  const subRoutes = [
    { name: 'Dashboard', path: '/dashboard/accounting', end: true },
    { name: 'Transactions', path: '/dashboard/accounting/transactions' },
    { name: 'Reports', path: '/dashboard/accounting/reports' },
    { name: 'Tax', path: '/dashboard/accounting/tax' }
  ]

  return (
    <div className="accounting-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-info">
          <div className="module-badge accounting">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
            </svg>
          </div>
          <div>
            <h1 className="page-title">Accounting</h1>
            <p className="page-subtitle">Keeply · Track finances and manage your books</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4"/>
            </svg>
            Settings
          </button>
          <button className="btn btn-primary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Record transaction
          </button>
        </div>
      </div>

      {/* Sub Navigation */}
      <nav className="sub-nav">
        {subRoutes.map(route => (
          <NavLink 
            key={route.path} 
            to={route.path} 
            end={route.end}
            className={({ isActive }) => `sub-nav-link ${isActive ? 'active' : ''}`}
          >
            {route.name}
          </NavLink>
        ))}
      </nav>

      {/* Content */}
      {isOverview ? <AccountingDashboard /> : <Outlet />}
    </div>
  )
}

export default Accounting
